import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Header, Footer } from "../components/layout";
import { Container } from "../components/ui";
import { APP_STORE_URL, PLAY_STORE_URL } from "../utils/appLinks";
import { SEO } from "../components/SEO";
import { apiClient } from "../apis/apiClient";

export default function AppRedirect() {
  const [searchParams] = useSearchParams();
  const rawRef = searchParams.get("ref") || searchParams.get("code") || "";
  const referralCode = rawRef.trim().toUpperCase();

  const [copied, setCopied] = useState(false);
  const [deviceType, setDeviceType] = useState<"ios" | "android" | "desktop">("desktop");

  useEffect(() => {
    let detectedPlatform: "ios" | "android" | "desktop" = "desktop";
    if (typeof window !== "undefined") {
      const ua = navigator.userAgent || navigator.vendor || (window as any).opera || "";
      if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) {
        detectedPlatform = "ios";
      } else if (/android/i.test(ua)) {
        detectedPlatform = "android";
      }
      setDeviceType(detectedPlatform);
    }

    // Save referral code in localStorage & track click for deferred deep link auto-resolution
    if (referralCode) {
      try {
        localStorage.setItem("setgo_referral_code", referralCode);
        apiClient
          .post("/mobile/referral/track-click", {
            referralCode,
            platform: detectedPlatform,
          })
          .catch(() => {});
      } catch (e) {
        // ignore
      }
    }
  }, [referralCode]);

  const copyToClipboard = () => {
    if (!referralCode) return;
    navigator.clipboard.writeText(referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleDownloadClick = () => {
    // Proactively copy code to clipboard as user proceeds to download
    if (referralCode && navigator.clipboard) {
      navigator.clipboard.writeText(referralCode).catch(() => {});
    }
  };

  const getPlayStoreTarget = () => {
    if (!referralCode) return PLAY_STORE_URL;
    return `${PLAY_STORE_URL}&referrer=utm_source%3Dreferral%26utm_campaign%3D${encodeURIComponent(referralCode)}`;
  };

  const qrTargetUrl = typeof window !== "undefined" ? window.location.href : `https://setgoinnovations.com/app?ref=${referralCode}`;
  const qrCodeImageSrc = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(qrTargetUrl)}&color=55-47-181`;

  return (
    <>
      <SEO
        title="Download SetGo Mobile App | Exclusive Local Deals & Rewards"
        description="Download SetGo on Android and iOS. Discover nearby businesses, unlock exclusive discounts, and earn loyalty points on every visit."
      />
      <div className="min-h-screen bg-[#0F0721] text-white flex flex-col justify-between">
        <Header />

        <main className="flex-1 pt-28 pb-16 flex items-center justify-center relative overflow-hidden">
          {/* Ambient lighting */}
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple-600/15 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

          <Container size="md" padding="md" className="relative z-10 w-full max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-[#1A0E38]/90 border border-purple-500/25 rounded-3xl p-6 sm:p-10 shadow-2xl backdrop-blur-xl text-center"
            >
              {/* Logo / Badge */}
              <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-purple-600/20 border border-purple-400/30 mb-6">
                <img src="/Logo1.png" alt="SetGo Logo" className="h-7 sm:h-8 w-auto object-contain" />
              </div>

              {referralCode ? (
                <>
                  <span className="inline-block px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-400/15 text-amber-300 border border-amber-400/30 mb-3">
                    Special Referral Invite
                  </span>
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-2">
                    You've Been Invited to SetGo!
                  </h1>
                  <p className="text-purple-200/80 text-xs sm:text-sm max-w-md mx-auto mb-6">
                    Join with this referral code to unlock rewards on your purchases at top local stores across Kerala.
                  </p>

                  {/* Referral Code Card */}
                  <div className="bg-[#24134B] border border-purple-500/30 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-inner">
                    <div className="text-left">
                      <span className="text-[11px] font-semibold text-purple-300/70 uppercase tracking-wider block">
                        Your Referral Code
                      </span>
                      <span className="text-2xl font-black text-[#FFC727] tracking-widest font-mono">
                        {referralCode}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-purple-600 hover:bg-purple-500 transition-all active:scale-95 shadow-md"
                    >
                      {copied ? (
                        <>
                          <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <span>Copy Code</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-2">
                    Get the SetGo App
                  </h1>
                  <p className="text-purple-200/80 text-xs sm:text-sm max-w-md mx-auto mb-6">
                    Discover 400+ local shops, redeem real-time offers, and earn loyalty points everywhere you shop.
                  </p>
                </>
              )}

              {/* Download Buttons */}
              <div className="space-y-3 pt-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a
                    href={getPlayStoreTarget()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDownloadClick}
                    className="flex items-center justify-center gap-3 px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold text-xs sm:text-sm transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md"
                  >
                    <svg className="w-5 h-5 fill-current text-emerald-400" viewBox="0 0 24 24">
                      <path d="M3.609 1.814L13.792 12 3.61 22.186a2.213 2.213 0 01-.61-.92L3 2.734c0-.35.21-.692.609-.92zm11.604 11.607l2.585-2.586a1.18 1.18 0 000-1.67L15.213 6.58l-1.42 1.42 4.195 4.195-4.195 4.196 1.42 1.42zm-1.421-1.42L4.316 2.527l12.316 7.11-2.84 2.84zm0 2.84l2.84 2.84-12.316 7.11 9.476-9.95z" />
                    </svg>
                    <span>Google Play</span>
                  </a>

                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDownloadClick}
                    className="flex items-center justify-center gap-3 px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold text-xs sm:text-sm transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md"
                  >
                    <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.92-2.87-.91.04-2.02.61-2.67 1.37-.58.67-1.09 1.74-.96 2.78 1.02.08 2.08-.53 2.71-1.28" />
                    </svg>
                    <span>App Store</span>
                  </a>
                </div>

                {/* Primary Smart Button for Mobile */}
                <div className="pt-2">
                  <a
                    href={deviceType === "ios" ? APP_STORE_URL : getPlayStoreTarget()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDownloadClick}
                    className="w-full inline-flex items-center justify-center px-6 py-3.5 rounded-full text-sm font-black text-[#1E1B4B] bg-[#FBBF24] hover:bg-[#F59E0B] shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Install & Earn Rewards
                  </a>
                </div>
              </div>

              {/* Desktop QR Code Scan Card */}
              {deviceType === "desktop" && (
                <div className="mt-8 pt-6 border-t border-purple-500/20 flex flex-col items-center">
                  <span className="text-xs text-purple-200/70 mb-3 block">
                    Or scan this QR code with your phone camera to download:
                  </span>
                  <div className="p-3 bg-white rounded-2xl shadow-lg border border-purple-200 inline-block">
                    <img
                      src={qrCodeImageSrc}
                      alt="Download SetGo QR Code"
                      className="w-36 h-36 object-contain"
                    />
                  </div>
                </div>
              )}
            </motion.div>
          </Container>
        </main>

        <Footer />
      </div>
    </>
  );
}
